import { FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CircleDollarSign, Plus, X } from 'lucide-react';
import { adminAccountsApi } from '../../../api/admin/accounts';
import { ApiError } from '../../../api/client';
import { getAllowedRoles, type Role } from '../../../api/management';
import type { AdminAccountCreateInput } from '../../../types/admin-account';

const controlClass = 'admin-data-primary min-h-10 w-full rounded-xl border border-white/10 bg-[#111827] px-3 py-2 text-sm font-semibold outline-none transition placeholder:text-slate-500 focus:border-sky-300/35 focus:ring-2 focus:ring-sky-300/10';
const labelClass = 'admin-data-secondary grid gap-1.5 text-sm font-bold';

const initialForm: AdminAccountCreateInput = {
  username: '',
  password: '',
  displayName: '',
  role: 'SUPER_MASTER',
  contact: '0',
  address: '0',
  share: '0',
  safaCommission: '0',
  akraCommission: '0',
  tndolaCommission: '0',
  pcCommission: '0',
  safaPrize: '8',
  akraPrize: '80',
  tndolaPrize: '800',
  pcPrize: '6000',
  savedSheetDeleteAllowed: false,
};

export default function AccountCreatePage() {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [tab, setTab] = useState<'GENERAL' | 'PRIZE'>('GENERAL');
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(true);
  const [error, setError] = useState('');
  const [allowedRoles, setAllowedRoles] = useState<Role[]>([]);
  const [roleSelected, setRoleSelected] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void getAllowedRoles().then(({ roles }) => {
      if (cancelled) return;
      setAllowedRoles(roles);
      if (roles.includes('SUPER_MASTER')) {
        setForm((current) => ({ ...current, role: 'SUPER_MASTER' }));
        setRoleSelected(true);
      } else if (roles.length === 1) {
        setForm((current) => ({ ...current, role: roles[0] as AdminAccountCreateInput['role'] }));
        setRoleSelected(true);
      } else {
        setRoleSelected(false);
        if (roles.length) setForm((current) => ({ ...current, role: roles[0] as AdminAccountCreateInput['role'] }));
      }
    }).catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : 'Unable to load allowed roles'); });
    return () => { cancelled = true; };
  }, []);

  const update = <K extends keyof AdminAccountCreateInput>(key: K, value: AdminAccountCreateInput[K]) => setForm((current) => ({ ...current, [key]: value }));

  const createAccount = async (destination: 'dashboard' | 'payment') => {
    if (saving) return;
    if (!roleSelected) { setError('Please select a role.'); return; }
    if (form.username.trim().length < 4) { setError('Username must be at least 4 characters.'); return; }
    if (form.password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    if (!form.displayName.trim()) { setError('Display Name is required.'); return; }
    setSaving(true);
    setError('');
    try {
      const response = await adminAccountsApi.createAccount(form);
      if (destination === 'payment') {
        navigate(`/accounts/${encodeURIComponent(response.account.id)}/payment/`, { replace: true });
        return;
      }
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Account creation failed');
      setSaving(false);
    }
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    void createAccount('dashboard');
  };

  return (
    <form onSubmit={submit} className="mx-auto max-w-4xl">
      <section className="overflow-hidden rounded-3xl border border-white/10 bg-[rgba(18,26,43,0.88)] shadow-[0_18px_44px_rgba(0,0,0,0.34)] backdrop-blur-md">
        <div className="border-b border-white/[0.08] p-4 sm:p-5">
          <button type="button" onClick={() => navigate('/')} className="crystal-btn crystal-sapphire mb-3 inline-flex min-h-10 items-center gap-2 rounded-xl px-4 py-2 text-xs font-black uppercase tracking-[0.12em] text-white"><ArrowLeft size={16} aria-hidden="true" /><span>Back to Dashboard</span></button>
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">New Account</p>
          <h1 className="admin-text-strong mt-1 text-2xl font-black">Create Account</h1>
          <p className="mt-1 text-sm font-semibold text-slate-400">Available roles are controlled by the current account hierarchy.</p>
        </div>

        <div className="grid grid-cols-2 gap-2 border-b border-white/[0.08] bg-black/10 p-2.5">
          <button type="button" onClick={() => setTab('GENERAL')} className={`crystal-btn rounded-xl px-3 py-2.5 text-sm font-black ${tab === 'GENERAL' ? 'crystal-sapphire text-[#DCEAF2]' : 'crystal-smoky text-slate-400'}`}>General Info</button>
          <button type="button" onClick={() => setTab('PRIZE')} className={`crystal-btn rounded-xl px-3 py-2.5 text-sm font-black ${tab === 'PRIZE' ? 'crystal-violet text-[#DDD4E9]' : 'crystal-smoky text-slate-400'}`}>Prize Setting</button>
        </div>

        <div className="p-4 sm:p-5">
          {error ? <div className="mb-4 rounded-xl border border-rose-400/25 bg-rose-500/10 px-4 py-3 text-sm font-semibold text-rose-200">{error}</div> : null}
          {tab === 'GENERAL' ? (
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              {allowedRoles.length === 1 && allowedRoles[0] === 'USER' ? null : <label className={labelClass}>Role<select value={roleSelected ? form.role : ''} onChange={(e) => { update('role', e.target.value as AdminAccountCreateInput['role']); setRoleSelected(true); setError(''); }} className={controlClass}><option value="" disabled>Select Role</option>{allowedRoles.map((role) => <option key={role} value={role}>{role.replaceAll('_', ' ')}</option>)}</select></label>}
              <label className={labelClass}>Username<input value={form.username} onChange={(e) => update('username', e.target.value)} maxLength={150} className={controlClass} /></label>
              <label className={labelClass}>Password<div className="relative"><input type={showPassword ? 'text' : 'password'} value={form.password} onChange={(e) => update('password', e.target.value)} className={`${controlClass} pr-11`} /><button type="button" onClick={() => setShowPassword((v) => !v)} className="crystal-action-smoky absolute inset-y-0 right-0 w-10 rounded-r-xl text-slate-300">◉</button></div></label>
              <label className={labelClass}>Display Name<input value={form.displayName} onChange={(e) => update('displayName', e.target.value)} maxLength={150} className={controlClass} /></label>
              <label className={labelClass}>Contact<input value={form.contact} onChange={(e) => update('contact', e.target.value)} maxLength={50} className={controlClass} /></label>
              <label className={`${labelClass} col-span-2`}>Address<textarea value={form.address} onChange={(e) => update('address', e.target.value)} maxLength={2000} rows={3} className={`${controlClass} resize-y`} /></label>
              <label className={labelClass}>Safa Commission %<input inputMode="decimal" value={form.safaCommission} onChange={(e) => update('safaCommission', e.target.value)} className={controlClass} /></label>
              <label className={labelClass}>Akra Commission %<input inputMode="decimal" value={form.akraCommission} onChange={(e) => update('akraCommission', e.target.value)} className={controlClass} /></label>
              <label className={labelClass}>Tndola Commission %<input inputMode="decimal" value={form.tndolaCommission} onChange={(e) => update('tndolaCommission', e.target.value)} className={controlClass} /></label>
              <label className={labelClass}>PC Commission %<input inputMode="decimal" value={form.pcCommission} onChange={(e) => update('pcCommission', e.target.value)} className={controlClass} /></label>
              <label className={labelClass}>Share %<input inputMode="decimal" value={form.share} onChange={(e) => update('share', e.target.value)} className={controlClass} /></label>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <label className={labelClass}>Safa Prize<input inputMode="decimal" value={form.safaPrize} onChange={(e) => update('safaPrize', e.target.value)} className={controlClass} /></label>
              <label className={labelClass}>Akra Prize<input inputMode="decimal" value={form.akraPrize} onChange={(e) => update('akraPrize', e.target.value)} className={controlClass} /></label>
              <label className={labelClass}>Tndola Prize<input inputMode="decimal" value={form.tndolaPrize} onChange={(e) => update('tndolaPrize', e.target.value)} className={controlClass} /></label>
              <label className={labelClass}>PC Prize<input inputMode="decimal" value={form.pcPrize} onChange={(e) => update('pcPrize', e.target.value)} className={controlClass} /></label>
            </div>
          )}
        </div>

        <div className="grid grid-cols-3 gap-2 border-t border-white/[0.08] bg-black/10 p-3 sm:p-4">
          <button type="button" onClick={() => navigate('/')} className="crystal-btn crystal-smoky inline-flex min-h-8 items-center justify-center gap-1.5 rounded-lg px-2 py-1 text-[10px] font-bold text-[#D6DEE8] sm:text-xs"><X size={13} aria-hidden="true" />Cancel</button>
          <button type="submit" disabled={saving} className="crystal-btn crystal-sapphire inline-flex min-h-8 items-center justify-center gap-1.5 rounded-lg px-2 py-1 text-[10px] font-black text-[#DCEAF2] disabled:opacity-50 sm:text-xs"><Plus size={13} aria-hidden="true" />{saving ? 'Creating…' : 'Create Account'}</button>
          <button type="button" disabled={saving} onClick={() => void createAccount('payment')} className="crystal-btn crystal-emerald inline-flex min-h-8 items-center justify-center gap-1.5 rounded-lg px-2 py-1 text-[10px] font-black disabled:opacity-50 sm:text-xs"><CircleDollarSign size={13} aria-hidden="true" />{saving ? 'Creating…' : 'Add Balance'}</button>
        </div>
      </section>
    </form>
  );
}
